import { Badge } from "@/components/badge";
import { Card } from "@/components/card";
import { Skeleton } from "@/components/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/table";
import { ToggleGroup, ToggleGroupItem } from "@/components/toggle-group";
import { useEvidenceGatesCheck1, useEvidenceGatesCheck2 } from "@/http/api-hooks";
import {
  EvidenceGateExperiment,
  EvidenceGatePeriod,
  EvidenceGatesCheck1Response,
  EvidenceGatesCheck2Response,
} from "@/http/api-types";
import { useUrlParams } from "@/http/use-url-params";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { Check1Table } from "./check-1-section";
import {
  byConcludedDesc,
  experimentsInPeriod,
  formatPercent,
  formatRate,
  paceRatio,
  paceToneClass,
  verdictBreakdown,
} from "./check-2";
import { AVERAGE_OPTIONS, AverageOption, ThroughputChart } from "./throughput-chart";

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-lg font-bold leading-tight">{children}</h3>;
}

function SectionDescription({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}

function periodRange(period: EvidenceGatePeriod): string {
  return `${period.from.slice(0, 10)} → ${period.to.slice(0, 10)}`;
}

function StatTile({ label, subtitle, children }: { label: string; subtitle: string; children: React.ReactNode }) {
  return (
    <Card className="flex flex-col px-5 py-4">
      <SectionHeading>{label}</SectionHeading>
      <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
      <div className="mt-3 space-y-1">{children}</div>
    </Card>
  );
}

// RateRow leads with the weekly rate. `muted` is the all-experiments reading, same
// size as the trusted one above it but recessive, so the two read as a pair.
function RateRow({
  rate,
  ratio,
  label,
  muted,
}: {
  rate: number;
  ratio?: number | null;
  label: string;
  muted?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-2">
      <span className={cn("text-2xl font-semibold leading-none", muted && "text-muted-foreground")}>
        {formatRate(rate)}
      </span>
      <span className="text-xs text-muted-foreground">/wk</span>
      {ratio !== undefined && ratio !== null && (
        <span className={cn("text-sm font-semibold", paceToneClass(ratio))}>{formatPercent(ratio)}</span>
      )}
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

// CountRow is the Check 1 equivalent of RateRow: a count against a fixed target
// rather than a rate against a derived one, but the same shape so the two tiles read
// as a pair.
function CountRow({ achieved, required, label }: { achieved: number; required: number; label: string }) {
  const ratio = required > 0 ? achieved / required : null;
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-2xl font-semibold leading-none">{achieved}</span>
      <span className="text-xs text-muted-foreground">of {required}</span>
      {ratio !== null && (
        <span className={cn("text-sm font-semibold", paceToneClass(ratio))}>{formatPercent(ratio)}</span>
      )}
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

function PreRegistrationCell({ experiment }: { experiment: EvidenceGateExperiment }) {
  if (experiment.trust_pre_registration !== "fail") {
    return <span className="text-muted-foreground">{experiment.trust_pre_registration}</span>;
  }
  const recorded = experiment.recorded_after_start;
  return (
    <Badge variant={recorded ? "fail" : "warn"}>
      {recorded ? "recorded" : "edited"} {experiment.pre_registration_late_days}d after start
    </Badge>
  );
}

// The table scrolls horizontally, so ID and Name are frozen on the left. Their cells
// need an opaque background or the scrolling columns show through; `bg-background`
// plus the same hover tint the row uses keeps them visually part of the row.
// The ID column width and the Name column offset must match exactly, or the browser
// shrinks the ID cell to its content and scrolled columns show through the gap.
// min-w pins it; w alone is only a hint in an auto-layout table.
const ID_WIDTH = "w-[44px] min-w-[44px] max-w-[44px]";
const STICKY_ID = `sticky left-0 z-20 ${ID_WIDTH} bg-background group-hover/row:bg-muted`;
const STICKY_NAME = "sticky left-[44px] z-20 max-w-[22rem] truncate border-r bg-background group-hover/row:bg-muted";

function ExperimentsTable({ experiments }: { experiments: EvidenceGateExperiment[] }) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="group/row">
            <TableHead className={STICKY_ID}>ID</TableHead>
            <TableHead className={STICKY_NAME}>Name</TableHead>
            <TableHead>Owner</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Verdict</TableHead>
            <TableHead>Pre-registered</TableHead>
            <TableHead className="text-right">Runtime</TableHead>
            <TableHead>Rollout</TableHead>
            <TableHead>Ended</TableHead>
            <TableHead>Concluded</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {byConcludedDesc(experiments).map(experiment => (
            <TableRow
              key={experiment.id}
              className={cn("group/row hover:bg-muted", !experiment.trusted && "text-muted-foreground")}
            >
              <TableCell className={cn(STICKY_ID, "tabular-nums")}>
                <Link to={`/experiments/${experiment.id}`} className="hover:underline">
                  {experiment.id}
                </Link>
              </TableCell>
              <TableCell className={STICKY_NAME} title={experiment.name}>
                <Link to={`/experiments/${experiment.id}`} className="block truncate hover:underline">
                  {experiment.name}
                </Link>
              </TableCell>
              <TableCell className="whitespace-nowrap">{experiment.owner_name || "—"}</TableCell>
              <TableCell>{experiment.type}</TableCell>
              <TableCell>
                {experiment.trusted ? (
                  <Badge variant="violet">Trusted</Badge>
                ) : (
                  <span className="text-xs">{experiment.verdict_detail}</span>
                )}
              </TableCell>
              <TableCell>
                <PreRegistrationCell experiment={experiment} />
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {experiment.trust_runtime ? (
                  `${experiment.runtime_days}d`
                ) : (
                  <Badge variant="fail">{experiment.runtime_days}d</Badge>
                )}
              </TableCell>
              <TableCell>
                {experiment.rollout_recorded ? (
                  <span className="text-muted-foreground">recorded</span>
                ) : (
                  <Badge variant="yellow">awaiting</Badge>
                )}
              </TableCell>
              <TableCell className="tabular-nums">{experiment.end_date.slice(0, 10)}</TableCell>
              <TableCell className="tabular-nums">{experiment.concluded_at.slice(0, 10)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// The two checks load from different places — Check 2 from Postgres, Check 1 from
// BigQuery, which takes seconds — so each fills in on its own rather than one holding
// the whole page blank.
function TileSkeleton({ label, failed }: { label: string; failed: boolean }) {
  return (
    <Card className="flex flex-col px-5 py-4">
      <SectionHeading>{label}</SectionHeading>
      {failed ? (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400">Failed to load.</p>
      ) : (
        <>
          <Skeleton className="mt-1.5 h-3 w-2/3" />
          <div className="mt-4 space-y-2">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-6 w-40" />
          </div>
          <Skeleton className="mt-3 h-3 w-5/6" />
        </>
      )}
    </Card>
  );
}

function BlockSkeleton({ rows, failed }: { rows: number; failed: boolean }) {
  if (failed) {
    return <p className="text-sm text-red-600 dark:text-red-400">Failed to load.</p>;
  }
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className="h-8 w-full" />
      ))}
    </div>
  );
}

function Check1Tile({ data }: { data: EvidenceGatesCheck1Response }) {
  return (
    <StatTile
      label="Check 1 Achievement"
      subtitle={`needs ${data.rollouts_required} rollouts · ${data.wins_required} wins · whole 2026 → ${data.gate_date.slice(
        0,
        10,
      )}`}
    >
      <CountRow achieved={data.rolled_out} required={data.rollouts_required} label="rolled out" />
      <CountRow achieved={data.wins} required={data.wins_required} label="wins" />
      <p className="pt-1 text-xs text-muted-foreground">
        win: XPC ≥{Math.round(data.lift_threshold * 100)}% better in week 2 vs experiment control
      </p>
      <p className="text-xs text-muted-foreground">
        {data.unmeasurable > 0 ? `${data.unmeasurable} with no week 2 traffic · ` : ""}reverts not tracked yet
      </p>
    </StatTile>
  );
}

function Check2Tile({ data }: { data: EvidenceGatesCheck2Response }) {
  return (
    <StatTile
      label="Check 2 Achievement"
      subtitle={`needs ${data.multiplier}× baseline · ${periodRange(data.evaluation_to_date)}`}
    >
      <RateRow
        rate={data.evaluation_to_date.trusted_per_week}
        ratio={paceRatio(data.evaluation_to_date.trusted_per_week, data.target_per_week)}
        label="trusted"
      />
      <RateRow
        rate={data.evaluation_to_date.raw_per_week}
        ratio={paceRatio(data.evaluation_to_date.raw_per_week, data.target_raw_per_week)}
        label="concluded"
        muted
      />
      <p className="pt-1 text-xs text-muted-foreground">
        baseline → target: {formatRate(data.baseline.trusted_per_week)} → {formatRate(data.target_per_week)} trusted ·{" "}
        {formatRate(data.baseline.raw_per_week)} → {formatRate(data.target_raw_per_week)} concluded
      </p>
      <p className="text-xs text-muted-foreground">
        {data.evaluation_to_date.trusted} trusted · {data.evaluation_to_date.raw} concluded
        {data.evaluation_to_date.awaiting_rollout > 0 &&
          ` · ${data.evaluation_to_date.awaiting_rollout} awaiting rollout`}
      </p>
    </StatTile>
  );
}

export default function EvidenceGatesPage() {
  const { data, error } = useEvidenceGatesCheck2();
  const { data: check1, error: check1Error } = useEvidenceGatesCheck1();
  const { paramState, updateParam } = useUrlParams({
    average: {
      name: "average",
      type: "enum" as const,
      validValues: AVERAGE_OPTIONS.map(option => option.value),
      defaultValue: "avg4",
    },
  });
  const average = paramState.average as AverageOption;

  const excluded = data
    ? verdictBreakdown(experimentsInPeriod(data.experiments, data.evaluation)).filter(
        entry => entry.verdict !== "counts",
      )
    : [];

  return (
    <div className="flex flex-col gap-10">
      <div className="grid grid-cols-2 gap-3">
        {check1 ? <Check1Tile data={check1} /> : <TileSkeleton label="Check 1 Achievement" failed={!!check1Error} />}
        {data ? <Check2Tile data={data} /> : <TileSkeleton label="Check 2 Achievement" failed={!!error} />}
      </div>

      <section className="flex flex-col gap-4">
        <div className="space-y-1.5">
          <SectionHeading>Check 1 – Rolled-Out Experiment Wins</SectionHeading>
          {check1 ? (
            <SectionDescription>
              XPC in week 2 after the rollout vs the experiment control, per audience the control actually delivered to.
              A rollout after {check1.last_durable_rollout.slice(0, 10)} cannot prove {check1.durability_days} days
              before the gate.
            </SectionDescription>
          ) : (
            <Skeleton className="h-4 w-3/4" />
          )}
        </div>
        {check1 && !check1.realized_available && (
          <p className="text-sm text-red-600 dark:text-red-400">
            Realized performance could not be read from the warehouse — the figures below are missing, not zero.
          </p>
        )}
        {check1 ? <Check1Table data={check1} /> : <BlockSkeleton rows={3} failed={!!check1Error} />}
      </section>

      <section className="flex flex-col gap-4">
        {/* Same cap as the chart below, so the trend toggle stays on the chart's right
            edge instead of drifting to the far side of a wide screen. */}
        <div className="flex w-full max-w-5xl items-start justify-between gap-4">
          <div className="space-y-1.5">
            <SectionHeading>Check 2 – Experiment Throughput</SectionHeading>
            {data ? (
              <SectionDescription>
                Experiments with a written conclusion, counted in the week the conclusion was written. Trusted
                experiments additionally ran at least {data.min_runtime_days} calendar days and had their hypothesis
                fixed within {data.pre_registration_grace_hours} hours of the start. Rollout decisions are covered by
                Check 1.
              </SectionDescription>
            ) : (
              <Skeleton className="h-4 w-3/4" />
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="text-xs text-muted-foreground">Trend</span>
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={average}
              onValueChange={value => {
                if (value) updateParam("average", value);
              }}
            >
              {AVERAGE_OPTIONS.map(option => (
                <ToggleGroupItem key={option.value} value={option.value} className="min-w-fit px-3">
                  {option.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
        </div>
        {/* Capped: the tables want the full width, but stretching the chart across a
            wide screen just spreads sixteen weekly bars thinner. */}
        {data ? (
          <div className="w-full max-w-5xl">
            <ThroughputChart
              data={data.weekly}
              targetPerWeek={data.target_per_week}
              targetRawPerWeek={data.target_raw_per_week}
              average={average}
            />
          </div>
        ) : error ? (
          <p className="text-sm text-red-600 dark:text-red-400">Failed to load.</p>
        ) : (
          <Skeleton className="h-[280px] w-full max-w-5xl" />
        )}
      </section>

      {excluded.length > 0 && (
        <section className="flex flex-col gap-4">
          <div className="space-y-1.5">
            <SectionHeading>Why experiments are not trusted</SectionHeading>
            <SectionDescription>
              Evaluation window only — the binding constraint on trusted throughput.
            </SectionDescription>
          </div>
          <ul className="flex max-w-md flex-col text-sm">
            {excluded.map(entry => (
              <li key={entry.verdict} className="flex justify-between border-b py-1 last:border-b-0">
                <span>{entry.verdict}</span>
                <span className="font-medium tabular-nums">{entry.count}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="flex flex-col gap-4">
        <div className="space-y-1.5">
          <SectionHeading>Concluded experiments</SectionHeading>
          {data ? (
            <SectionDescription>
              Every experiment with a conclusion from {data.baseline.from.slice(0, 10)} onward, most recently concluded
              first, greyed out when it does not count toward trusted throughput.
            </SectionDescription>
          ) : (
            <Skeleton className="h-4 w-3/4" />
          )}
        </div>
        {data ? <ExperimentsTable experiments={data.experiments} /> : <BlockSkeleton rows={6} failed={!!error} />}
      </section>
    </div>
  );
}
